import { translate } from "app/filters/i18nFilter";
import { UNKNOWN_COUNTRY } from "app/pages/sales-intelligence/sub-modules/signals/constants/common";
import type { SignalNameSegments } from "app/pages/sales-intelligence/sub-modules/signals/models/miscellaneous/SignalNameSegments";
import type SignalNameWithCountry from "app/pages/sales-intelligence/sub-modules/signals/models/miscellaneous/SignalNameWithCountry";
import type { TranslatableNameWithType } from "app/pages/sales-intelligence/sub-modules/signals/models/services/FilterNamingService";
import { FilterType } from "app/pages/sales-intelligence/sub-modules/signals/models/services/FilterService";
import { isTrafficChangesModel } from "app/pages/sales-intelligence/sub-modules/signals/models/traffic/type-guards/isTrafficChangesModel";
import { isTrafficMarketModel } from "app/pages/sales-intelligence/sub-modules/signals/models/traffic/type-guards/isTrafficMarketModel";
import filterService from "app/pages/sales-intelligence/sub-modules/signals/services/filters/filterService";
import signalsService from "app/pages/sales-intelligence/sub-modules/signals/services/signalsService";
import type { Translatable } from "app/pages/sales-intelligence/types";

import type Signal from "../../../models/core/Signal";
import type TrafficSignalFilters from "../../../models/traffic/TrafficSignalFilters";
import TrafficSignalSubType from "../../../models/traffic/TrafficSignalSubType";
import type UISignalDataDeps from "../../../models/view/UISignalDataDeps";
import type UISignalNameBuilder from "../UISignalNameBuilder";
import {
    filterSelectionTranslatable,
    isPartialFilterName,
} from "app/pages/sales-intelligence/sub-modules/signals/models/services/filterSelectionNaming";
import {
    boldFilterSelectionTranslatable,
    boldHtml,
    boldTranslatable,
} from "app/pages/sales-intelligence/sub-modules/signals/utils/boldSignalValue";

import trafficFiltersService from "./trafficSignalFiltersService";

const NOT_ENOUGH_DATA_ERROR_MESSAGE = "Not enough data to build traffic signal name";

function buildPeriodName<
    F extends {
        readonly period: string;
    },
>(filters: F): Translatable {
    const { period } = filters;

    return {
        key: `si.signals.changing-traction.${period}.label`,
    };
}

function buildTrafficMetricName<
    F extends {
        readonly metric: string;
    },
>(filters: F): string {
    const { metric } = filters;

    return translate(`si.signals.filters.traffic-metric.${metric}.name`);
}

function buildCountriesFilterName<
    F extends {
        countries: readonly number[];
    },
>(filters: F, userCountries: UISignalDataDeps["countries"]): Readonly<TranslatableNameWithType> {
    const nameBuilder = filterService.getFilterName(FilterType.Countries);

    return nameBuilder.buildName(filters, userCountries);
}

function buildCountriesName<
    F extends {
        countries: readonly number[];
    },
>(filters: F, userCountries: UISignalDataDeps["countries"]): Translatable {
    return boldFilterSelectionTranslatable(buildCountriesFilterName(filters, userCountries));
}

function buildCountriesSegmentName<
    F extends {
        countries: readonly number[];
    },
>(filters: F, userCountries: UISignalDataDeps["countries"]): Translatable {
    return filterSelectionTranslatable(buildCountriesFilterName(filters, userCountries));
}

function getCountryName(countryId: number | undefined): string {
    let countryName: string;

    try {
        countryName = signalsService.getSignalCountryById(countryId).text;
    } catch (e) {
        countryName = UNKNOWN_COUNTRY;
    }

    return countryName;
}

export default function createTrafficUISignalNameBuilder(
    deps: Pick<UISignalDataDeps, "countries">,
): UISignalNameBuilder<TrafficSignalFilters> {
    /* @throws Error */
    function buildTractionName(signal: Signal<TrafficSignalFilters>): Readonly<SignalNameWithCountry> {
        const {
            filters: { trafficChanges },
        } = signal;

        const isNecessaryDataPresent = isTrafficChangesModel(trafficChanges);
        if (!isNecessaryDataPresent) {
            throw new Error(NOT_ENOUGH_DATA_ERROR_MESSAGE);
        }

        const trafficCountryName = getCountryName(trafficChanges.country);

        const tractionChangeName = buildPeriodName(trafficChanges);
        const trafficChannelName = buildTrafficMetricName(trafficChanges);
        const trafficChangeValue = trafficFiltersService.getChangeValue(trafficChanges);

        return {
            key: `si.signals.traffic-changes-direction.${trafficChanges?.direction}.label.with-country`,
            replacements: {
                countryCode: trafficChanges?.country,
                country: boldHtml(trafficCountryName),
                value: trafficChangeValue,
                period: boldTranslatable(tractionChangeName),
                channelName: boldHtml(trafficChannelName.toLowerCase()),
            },
        };
    }

    /* @throws Error */
    function buildTractionNameSegments(signal: Signal<TrafficSignalFilters>): SignalNameSegments {
        const {
            filters: { trafficChanges },
        } = signal;

        const isNecessaryDataPresent = isTrafficChangesModel(trafficChanges);
        if (!isNecessaryDataPresent) {
            throw new Error(NOT_ENOUGH_DATA_ERROR_MESSAGE);
        }

        const trafficCountryName = getCountryName(trafficChanges.country);
        const tractionChangeName = buildPeriodName(trafficChanges);
        const trafficChannelName = buildTrafficMetricName(trafficChanges);
        const trafficChangeValue = trafficFiltersService.getChangeValue(trafficChanges);

        return {
            signalType: {
                key: `si.signals.segments.traffic-changes-direction.${trafficChanges?.direction}`,
                replacements: {
                    countryCode: trafficChanges?.country,
                    country: trafficCountryName,
                    channelName: trafficChannelName.toLowerCase(),
                },
            },
            signalData: {
                key: `si.signals.segments.data.traffic-changes-direction.${trafficChanges?.direction}`,
                replacements: {
                    value: trafficChangeValue,
                    period: tractionChangeName,
                },
            },
        };
    }

    /* @throws Error */
    function buildNewMarketsName(signal: Signal<TrafficSignalFilters>): Readonly<SignalNameWithCountry> {
        const {
            filters: { newMarkets },
        } = signal;

        if (!isTrafficMarketModel(newMarkets)) {
            throw new Error(NOT_ENOUGH_DATA_ERROR_MESSAGE);
        }

        const trafficChannelName = buildTrafficMetricName(newMarkets);
        const thresholdName = trafficFiltersService.getThresholdValue(newMarkets);

        const countriesName = buildCountriesName(newMarkets, deps.countries);

        return {
            key: `si.signals.${signal.signalSubType}.name`,
            replacements: {
                metric: boldHtml(trafficChannelName),
                thresholdValue: thresholdName,
                countries: countriesName,
            },
        };
    }

    /* @throws Error */
    function buildNewMarketsNameSegments(signal: Signal<TrafficSignalFilters>): SignalNameSegments {
        const {
            filters: { newMarkets },
        } = signal;

        if (!isTrafficMarketModel(newMarkets)) {
            throw new Error(NOT_ENOUGH_DATA_ERROR_MESSAGE);
        }

        const trafficChannelName = buildTrafficMetricName(newMarkets);
        const thresholdName = trafficFiltersService.getThresholdValue(newMarkets);

        const countriesName = buildCountriesSegmentName(newMarkets, deps.countries);
        const countriesFilterName = buildCountriesFilterName(newMarkets, deps.countries);

        return {
            signalType: {
                key: `si.signals.segments.${signal.signalSubType}`,
                replacements: {
                    metric: trafficChannelName,
                },
            },
            signalData: {
                key: `si.signals.segments.data.${signal.signalSubType}`,
                replacements: {
                    thresholdValue: thresholdName,
                    countries: countriesName,
                },
            },
            ...(isPartialFilterName(countriesFilterName) ? { selectionOverflowOn: "signalData" as const } : {}),
        };
    }

    return {
        buildName(signal: Signal<TrafficSignalFilters>): Readonly<Translatable> {
            switch (signal.signalSubType) {
                case TrafficSignalSubType.GettingTraction:
                case TrafficSignalSubType.LosingTraction:
                case TrafficSignalSubType.Engagement:
                    return buildTractionName(signal);

                case TrafficSignalSubType.NewMarkets:
                    return buildNewMarketsName(signal);

                default:
                    throw new Error(`Couldn't build name for signalSubType: ${signal.signalSubType}`);
            }
        },
        buildNameSegments(signal: Signal<TrafficSignalFilters>) {
            switch (signal.signalSubType) {
                case TrafficSignalSubType.GettingTraction:
                case TrafficSignalSubType.LosingTraction:
                case TrafficSignalSubType.Engagement:
                    return buildTractionNameSegments(signal);

                case TrafficSignalSubType.NewMarkets:
                    return buildNewMarketsNameSegments(signal);

                default:
                    throw new Error(`Couldn't build name segments for signalSubType: ${signal.signalSubType}`);
            }
        },
        buildSelectionTooltipLabels(signal: Signal<TrafficSignalFilters>) {
            if (signal.signalSubType !== TrafficSignalSubType.NewMarkets) {
                return undefined;
            }

            const { newMarkets } = signal.filters;

            if (!isTrafficMarketModel(newMarkets)) {
                return undefined;
            }

            const countriesFilterName = buildCountriesFilterName(newMarkets, deps.countries);

            if (!isPartialFilterName(countriesFilterName)) {
                return undefined;
            }

            const labels = deps.countries.filter(({ id }) => newMarkets.countries.includes(id)).map(({ text }) => text);

            return labels.length > 1 ? { signalData: labels } : undefined;
        },
    };
}
